import { CARDAPIO_MESAS_PADRAO, PLANOS_CARDAPIO, slugificarNome } from '../../config/cardapio';
import type { ItemCardapio, PedidoCardapio, RestauranteCardapio } from '../../types/cardapio';

const LS_TENANTS = 'ade-cardapio-tenants';

function lsMenu(slug: string) {
  return `ade-cardapio-menu-${slug}`;
}
function lsPedidos(slug: string) {
  return `ade-cardapio-pedidos-${slug}`;
}

function lerJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function salvarJson(key: string, data: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* quota */
  }
}

const criarId = () => `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ——— Restaurantes (tenants) ———

export function listarRestaurantes(): RestauranteCardapio[] {
  return lerJson<RestauranteCardapio[]>(LS_TENANTS, []);
}

export function buscarRestaurantePorSlug(slug: string): RestauranteCardapio | null {
  const s = slug.trim().toLowerCase();
  return listarRestaurantes().find((r) => r.slug === s) ?? null;
}

export function salvarRestaurante(r: RestauranteCardapio): void {
  const lista = listarRestaurantes();
  const idx = lista.findIndex((x) => x.id === r.id);
  if (idx >= 0) lista[idx] = r;
  else lista.push(r);
  salvarJson(LS_TENANTS, lista);
}

export function excluirRestaurante(id: string): void {
  const r = listarRestaurantes().find((x) => x.id === id);
  if (r) {
    localStorage.removeItem(lsMenu(r.slug));
    localStorage.removeItem(lsPedidos(r.slug));
  }
  salvarJson(
    LS_TENANTS,
    listarRestaurantes().filter((x) => x.id !== id)
  );
}

export function criarRestauranteVazio(nome: string): RestauranteCardapio {
  const plano = PLANOS_CARDAPIO.basico;
  const hoje = new Date();
  const pagoAte = new Date(hoje.getFullYear(), hoje.getMonth() + 1, hoje.getDate());
  const pagoAteStr = pagoAte.toISOString().slice(0, 10);
  let slug = slugificarNome(nome) || 'restaurante';
  const existentes = new Set(listarRestaurantes().map((r) => r.slug));
  let n = 1;
  const base = slug;
  while (existentes.has(slug)) {
    slug = `${base}-${n++}`;
  }
  return {
    id: criarId(),
    slug,
    nome: nome.trim() || 'Novo restaurante',
    whatsapp: '',
    plano: 'basico',
    maxMesas: CARDAPIO_MESAS_PADRAO,
    modoCozinha: plano.modoCozinha,
    chavePix: '',
    senhaPainel: '1234',
    pagoAte: pagoAteStr,
    ativo: true,
    criadoEm: new Date().toISOString(),
  };
}

// ——— Cardápio (itens) ———

export function listarItensMenu(slug: string): ItemCardapio[] {
  return lerJson<ItemCardapio[]>(lsMenu(slug), []);
}

export function salvarItensMenu(slug: string, itens: ItemCardapio[]): void {
  salvarJson(lsMenu(slug), itens);
}

export function itensMenuAtivos(slug: string): ItemCardapio[] {
  return listarItensMenu(slug)
    .filter((i) => i.ativo)
    .sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome, 'pt-BR'));
}

// ——— Pedidos ———

export function listarPedidos(slug: string): PedidoCardapio[] {
  return lerJson<PedidoCardapio[]>(lsPedidos(slug), []);
}

export function salvarPedidos(slug: string, pedidos: PedidoCardapio[]): void {
  salvarJson(lsPedidos(slug), pedidos);
}

export function upsertPedido(slug: string, pedido: PedidoCardapio): void {
  const lista = listarPedidos(slug);
  const idx = lista.findIndex((p) => p.id === pedido.id);
  if (idx >= 0) lista[idx] = pedido;
  else lista.unshift(pedido);
  salvarPedidos(slug, lista);
}

/** Tempo estimado: maior prep dos itens + fila na cozinha. */
export function estimarTempoMin(slug: string, pedido: PedidoCardapio): number {
  const menu = listarItensMenu(slug);
  let prep = 5;
  pedido.itens.forEach((it) => {
    const m = menu.find((x) => x.id === it.itemId);
    if (m) prep = Math.max(prep, m.tempoPrepMin);
  });
  const fila = listarPedidos(slug).filter((p) =>
    ['pago', 'enviado_cozinha', 'preparando'].includes(p.status)
  ).length;
  return prep + fila * 4;
}
